/**
 * React Query hooks for managing Root KPI data.
 * These hooks provide clean interfaces for fetching, caching, and mutating Root KPIs using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import {
  AuditHistory,
  CreateRootKPIInput,
  CreateRootKPIMutationVariables,
  DeleteRootKPIMutationVariables,
  GetKPIHierarchyAuditHistoryQueryVariables,
  GetRootKPIsQuery,
  GetRootKPIsQueryVariables,
  GetRootKPIUsageSummaryQueryVariables,
  KPIUsageSummary,
  KPIVariant,
  RootKPI,
  UpdateRootKPIInput,
  UpdateRootKPIMutationVariables,
} from '@services/api/deckManagement/graphql/schema/API';
import { createRootKPI, deleteRootKPI, updateRootKPI } from '@services/api/deckManagement/graphql/schema/mutations';
import {
  getKPIHierarchyAuditHistory,
  getRootKPIUsageSummary,
} from '@services/api/deckManagement/graphql/schema/queries';
import {
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import {
  createQueryOrMutationKey,
  createStandardInfiniteQueryOptions,
  createStandardMutationOptions,
  createStandardQueryOptions,
  StandardMutationOptions,
  StandardQueryOptions,
} from '@utils/reactQueryConfig.ts';

/**
 * Interface for useGetRootKPIs hook parameters
 * @param root_kpi_id - Optional specific Root KPI ID to fetch
 * @param variantFields - Optional array of KPIVariant fields to include in the nested variants sub-query. If omitted, variants are not fetched.
 * @param options - Additional React Query options
 */
export interface UseGetRootKPIsParams extends GetRootKPIsQueryVariables {
  variantFields?: Array<keyof KPIVariant>;
  options?: StandardQueryOptions;
}

/**
 * Interface for useGetRootKPIUsageSummary hook parameters
 * @param root_kpi_id - The Root KPI ID to fetch usage summary for
 * @param options - Additional React Query options
 */
export interface UseGetRootKPIUsageSummaryParams extends GetRootKPIUsageSummaryQueryVariables {
  options?: StandardQueryOptions;
}

/**
 * Interface for useGetKPIAuditHistory hook parameters
 * @param root_kpi_id - The Root KPI ID to fetch audit history for
 * @param options - Additional React Query options
 */
export interface UseGetKPIAuditHistoryParams extends GetKPIHierarchyAuditHistoryQueryVariables {
  options?: StandardQueryOptions;
}

/**
 * Interface for useDeleteRootKPI hook parameters
 * @param root_kpi_id - The Root KPI ID to delete
 */
export type UseDeleteRootKPIParams = Pick<DeleteRootKPIMutationVariables, 'root_kpi_id'>;

// Query keys for Root KPI queries related caching
export const rootKPIQueryKeys = {
  all: createQueryOrMutationKey('rootKPI'),
  list: () => createQueryOrMutationKey(...rootKPIQueryKeys.all, 'list'),
  detail: (rootKpiId: string) => createQueryOrMutationKey(...rootKPIQueryKeys.all, rootKpiId),
  rootKPIUsageSummary: (rootKpiId: string) =>
    createQueryOrMutationKey(...rootKPIQueryKeys.all, 'usageSummary', rootKpiId),
  auditHistory: (rootKpiId: string) => createQueryOrMutationKey(...rootKPIQueryKeys.all, 'auditHistory', rootKpiId),
};

// Mutation keys for Root KPI operations
export const rootKPIMutationKeys = {
  all: createQueryOrMutationKey('rootKPI', 'mutation'),
  create: () => createQueryOrMutationKey(...rootKPIMutationKeys.all, 'create'),
  update: () => createQueryOrMutationKey(...rootKPIMutationKeys.all, 'update'),
  delete: () => createQueryOrMutationKey(...rootKPIMutationKeys.all, 'delete'),
};

/**
 * Hook to fetch Root KPIs. If a root_kpi_id is provided, fetches a single root KPI.
 * Otherwise, fetches all Root KPIs by paginating through all available pages.
 */
export const useGetRootKPIs = ({ root_kpi_id, variantFields, options }: UseGetRootKPIsParams = {}): UseQueryResult<
  RootKPI[]
> => {
  const { deckManagementClient } = useApiClients();

  // Build a dynamic query with optional variant sub-selection
  const variantSubQuery = variantFields?.length
    ? `variants {\n        ${variantFields.join('\n        ')}\n        __typename\n      }`
    : '';

  const dynamicQuery = `
    query GetRootKPIs($userAlias: String, $root_kpi_id: String, $nextToken: String) {
      getRootKPIs(userAlias: $userAlias, root_kpi_id: $root_kpi_id, nextToken: $nextToken) {
        items {
          pk
          sk
          root_kpi_id
          root_kpi_name
          kpi_type
          ${variantSubQuery}
          created_date
          created_by
          last_updated_date
          last_updated_by
          __typename
        }
        nextToken
        __typename
      }
    }
  `;

  return useQuery({
    queryKey: root_kpi_id
      ? [...rootKPIQueryKeys.detail(root_kpi_id), variantFields]
      : [...rootKPIQueryKeys.list(), variantFields],
    queryFn: async (): Promise<RootKPI[]> => {
      const variables: GetRootKPIsQueryVariables = {
        ...(root_kpi_id && { root_kpi_id }),
      };

      let allItems: RootKPI[] = [];
      let currentNextToken: string | null | undefined = null;

      do {
        const paginatedVariables: GetRootKPIsQueryVariables = {
          ...variables,
          ...(currentNextToken && { nextToken: currentNextToken }),
        };

        const response = await deckManagementClient.graphql({
          query: dynamicQuery,
          variables: paginatedVariables,
        });

        if ('data' in response && response.data?.getRootKPIs) {
          const paginatedResponse = response.data.getRootKPIs as GetRootKPIsQuery['getRootKPIs'];
          // Cast rather than a type predicate — see useKPIVariants: the generated document widens
          // `__typename` on nested GalaxyKPIDrivers unions, so the predicate is not assignable.
          const items = (paginatedResponse?.items?.filter((item) => item !== null) ?? []) as RootKPI[];
          allItems = [...allItems, ...items];
          currentNextToken = paginatedResponse?.nextToken;
        } else {
          throw new Error('Failed to fetch root KPIs');
        }
      } while (currentNextToken);

      return allItems;
    },
    ...createStandardQueryOptions<RootKPI[]>({ ...options, rumOp: extractGraphqlOperationName(dynamicQuery) }),
  });
};

/**
 * Hook to fetch usage summary for a Root KPI, showing which decks and pages reference it.
 */
export const useGetRootKPIUsageSummary = ({
  root_kpi_id,
  options,
}: UseGetRootKPIUsageSummaryParams): UseQueryResult<KPIUsageSummary | null> => {
  const { deckManagementClient } = useApiClients();

  const { enabled: enabledOption, ...restOptions } = options || {};
  const isEnabled = !!root_kpi_id && (enabledOption ?? true);

  return useQuery({
    queryKey: rootKPIQueryKeys.rootKPIUsageSummary(root_kpi_id),
    queryFn: async (): Promise<KPIUsageSummary | null> => {
      const response = await deckManagementClient.graphql({
        query: getRootKPIUsageSummary,
        variables: { root_kpi_id },
      });

      if ('data' in response && response.data?.getRootKPIUsageSummary) {
        return response.data.getRootKPIUsageSummary as KPIUsageSummary;
      }

      throw new Error('Failed to fetch root KPI usage summary');
    },
    ...createStandardQueryOptions<KPIUsageSummary | null>({
      ...restOptions,
      rumOp: extractGraphqlOperationName(getRootKPIUsageSummary),
    }),
    enabled: isEnabled,
  });
};

/**
 * Hook to fetch audit history for KPI hierarchy entities.
 */
export const useGetKPIAuditHistory = ({ entities, limit = 50, options }: UseGetKPIAuditHistoryParams) => {
  const { deckManagementClient } = useApiClients();

  const { enabled: enabledOption, ...restOptions } = options || {};
  const isEnabled = !!entities?.length && (enabledOption ?? true);

  return useInfiniteQuery({
    queryKey: rootKPIQueryKeys.auditHistory(JSON.stringify(entities)),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<{ items: AuditHistory[]; nextToken?: string }> => {
      const response = await deckManagementClient.graphql({
        query: getKPIHierarchyAuditHistory,
        variables: { entities, limit, nextToken: pageParam },
      });

      if ('data' in response && response.data?.getKPIHierarchyAuditHistory) {
        const auditItems = response.data.getKPIHierarchyAuditHistory.items;
        return {
          items: auditItems?.filter((item): item is AuditHistory => item !== null) ?? [],
          nextToken: response.data.getKPIHierarchyAuditHistory.nextToken ?? undefined,
        };
      }

      throw new Error('Failed to fetch KPI audit history');
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { items: AuditHistory[]; nextToken?: string }) => lastPage.nextToken ?? undefined,
    ...createStandardInfiniteQueryOptions({
      ...restOptions,
      enabled: isEnabled,
      rumOp: extractGraphqlOperationName(getKPIHierarchyAuditHistory),
    }),
  });
};

/**
 * Hook to create a new Root KPI.
 * Invalidates the root KPI list cache on success.
 */
export const useCreateRootKPI = (
  options?: StandardMutationOptions<RootKPI, Error, CreateRootKPIInput>,
): UseMutationResult<RootKPI, Error, CreateRootKPIInput> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: rootKPIMutationKeys.create(),
    mutationFn: async (params: CreateRootKPIInput): Promise<RootKPI> => {
      const response = await deckManagementClient.graphql({
        query: createRootKPI,
        variables: { request: params } as CreateRootKPIMutationVariables,
      });

      if ('data' in response && response.data?.createRootKPI) {
        return response.data.createRootKPI as RootKPI;
      }

      throw new Error('Failed to create root KPI');
    },
    ...createStandardMutationOptions<RootKPI, Error, CreateRootKPIInput>({
      ...options,
      rumOp: extractGraphqlOperationName(createRootKPI),
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: rootKPIQueryKeys.list() });
        options?.onSuccess?.(data, variables);
      },
    }),
  });
};

/**
 * Hook to update (rename) an existing Root KPI.
 * Invalidates the root KPI list and detail caches on success.
 */
export const useUpdateRootKPI = (
  options?: StandardMutationOptions<RootKPI, Error, UpdateRootKPIInput>,
): UseMutationResult<RootKPI, Error, UpdateRootKPIInput> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: rootKPIMutationKeys.update(),
    mutationFn: async (params: UpdateRootKPIInput): Promise<RootKPI> => {
      const response = await deckManagementClient.graphql({
        query: updateRootKPI,
        variables: { request: params } as UpdateRootKPIMutationVariables,
      });

      if ('data' in response && response.data?.updateRootKPI) {
        return response.data.updateRootKPI as RootKPI;
      }

      throw new Error('Failed to update root KPI');
    },
    ...createStandardMutationOptions<RootKPI, Error, UpdateRootKPIInput>({
      ...options,
      rumOp: extractGraphqlOperationName(updateRootKPI),
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: rootKPIQueryKeys.list() });
        queryClient.invalidateQueries({ queryKey: rootKPIQueryKeys.detail(variables.root_kpi_id) });
        options?.onSuccess?.(data, variables);
      },
    }),
  });
};

/**
 * Hook to delete a Root KPI.
 * Invalidates the root KPI list cache on success.
 */
export const useDeleteRootKPI = (
  options?: StandardMutationOptions<boolean, Error, UseDeleteRootKPIParams>,
): UseMutationResult<boolean, Error, UseDeleteRootKPIParams> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: rootKPIMutationKeys.delete(),
    mutationFn: async (params: UseDeleteRootKPIParams): Promise<boolean> => {
      const response = await deckManagementClient.graphql({
        query: deleteRootKPI,
        variables: params as DeleteRootKPIMutationVariables,
      });

      if ('data' in response && response.data?.deleteRootKPI) {
        return response.data.deleteRootKPI;
      }

      throw new Error('Failed to delete root KPI');
    },
    ...createStandardMutationOptions<boolean, Error, UseDeleteRootKPIParams>({
      ...options,
      rumOp: extractGraphqlOperationName(deleteRootKPI),
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: rootKPIQueryKeys.list() });
        options?.onSuccess?.(data, variables);
      },
    }),
  });
};
